import { useCallback, useEffect, useState } from 'react';
import type { Hold } from '../../types/climb';

/**
 * Which holds to draw over a climb image:
 * - 'startAndTop': only start (green) and top (red) holds — the default
 * - 'all': every hold
 * - 'none': hide all holds
 */
export type HoldsDisplayMode = 'startAndTop' | 'all' | 'none';

// Cycle order used by the on-image toggle button.
export const HOLDS_DISPLAY_MODES: HoldsDisplayMode[] = ['startAndTop', 'all', 'none'];
export const DEFAULT_HOLDS_DISPLAY_MODE: HoldsDisplayMode = 'startAndTop';

const HOLDS_MODE_STORAGE_KEY = 'climbProfile.holdsDisplayMode';

/** Read the persisted display mode (shared as the default across all climbs). */
export function readHoldsDisplayMode(): HoldsDisplayMode {
  try {
    const stored = localStorage.getItem(HOLDS_MODE_STORAGE_KEY);
    if (stored && (HOLDS_DISPLAY_MODES as string[]).includes(stored)) {
      return stored as HoldsDisplayMode;
    }
  } catch {
    /* ignore unavailable localStorage */
  }
  return DEFAULT_HOLDS_DISPLAY_MODE;
}

/** Persist the display mode so it becomes the default for every other climb. */
export function writeHoldsDisplayMode(mode: HoldsDisplayMode): void {
  try {
    localStorage.setItem(HOLDS_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore unavailable localStorage */
  }
}

/** start = green, top = red. */
export function holdShownInMode(hold: Hold, mode: HoldsDisplayMode): boolean {
  if (mode === 'none') return false;
  if (mode === 'all') return true;

  return hold.color === 'green' || hold.color === 'red';
}

export function holdsForMode(holds: Hold[], mode: HoldsDisplayMode): Hold[] {
  return holds.filter((hold) => holdShownInMode(hold, mode));
}

// Broadcast so every overlay on the page (e.g. the cover and the enlarged
// modal) stays in sync live when the mode is cycled.
const HOLDS_MODE_EVENT = 'holds-display-mode-change';

/**
 * Persisted, page-wide holds display mode. Returns the current mode and a
 * `cycle` callback advancing startAndTop → all → none → startAndTop. The mode
 * is stored in localStorage so it is the default for every other climb, and
 * all hook instances update together.
 */
export function useHoldsDisplayMode(): { mode: HoldsDisplayMode; cycle: () => void } {
  const [mode, setMode] = useState<HoldsDisplayMode>(readHoldsDisplayMode);

  useEffect(() => {
    const onChange = (e: Event) => setMode((e as CustomEvent<HoldsDisplayMode>).detail);
    window.addEventListener(HOLDS_MODE_EVENT, onChange);
    return () => window.removeEventListener(HOLDS_MODE_EVENT, onChange);
  }, []);

  const cycle = useCallback(() => {
    setMode((prev) => {
      const next = HOLDS_DISPLAY_MODES[(HOLDS_DISPLAY_MODES.indexOf(prev) + 1) % HOLDS_DISPLAY_MODES.length];
      writeHoldsDisplayMode(next);
      window.dispatchEvent(new CustomEvent(HOLDS_MODE_EVENT, { detail: next }));
      return next;
    });
  }, []);

  return { mode, cycle };
}
